import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
function EnglishContent() {
  return <>
      <h1 className="text-3xl md:text-5xl font-bold mb-8 text-[#2A4B7C]" data-id="element-130">
        Submit Your Work
      </h1>

      <p className="text-lg mb-2 font-semibold text-[#2A4B7C]" data-id="element-131">
        Guidelines - English
      </p>
      <p className="text-sm mb-8 text-[#334E68]" data-id="element-132">
        For Hebrew guidelines, check the Hebrew language version.
      </p>

      <div className="mb-8" data-id="element-133">
        <p className="mb-2 text-[#2F3A45]" data-id="element-134">
          In order to submit your work, email the following address:
        </p>
        <a href="mailto:submissions.stjournal@gmail.com" className="text-lg md:text-xl hover:text-amber-600 transition-colors font-semibold break-all text-[#2A4B7C]" data-id="element-135">
          submissions.stjournal@gmail.com
        </a>
      </div>

      <div className="mb-8" data-id="element-136">
        <p className="mb-2 text-[#334E68]" data-id="element-137">The title of the email should be:</p>
        <p className="font-semibold mb-2 text-[#2A4B7C]" data-id="element-138">
          Category + First Name + Surname.
        </p>
        <p className="italic mb-4 text-[#2F3A45]" data-id="element-139">
          Example: Flash Fiction + Mark + Johnson
        </p>
        <p className="mb-2 text-[#2A4B7C]" data-id="element-140">
          Please submit in the email of your language. For example, if you wrote
          a piece in Hebrew, make sure the title of the email is in Hebrew.
        </p>
        <p className="text-[#2F3A45]" data-id="element-141">
          The piece should be in a <strong data-id="element-142">PDF</strong> or <strong data-id="element-143">DOCX</strong>{' '}
          file.
        </p>
      </div>

      <div className="border-t border-gray-200 pt-8" data-id="element-144">
        <h2 className="text-2xl font-semibold mb-6 text-[#2A4B7C]" data-id="element-145">
          List of Categories
        </h2>

        {/* Fiction */}
        <div className="mb-8" data-id="element-146">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-147">Fiction</h3>
          <ul className="space-y-2 pl-6" data-id="element-148">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-149">
              <span className="text-amber-600 font-bold mt-1" data-id="element-150">•</span>
              <span className="text-[#2F3A45]" data-id="element-151">
                <strong className="text-[#374151]" data-id="element-152">Micro Fiction</strong> -
                Under 350 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-153">
              <span className="text-amber-600 font-bold mt-1" data-id="element-154">•</span>
              <span className="text-[#2F3A45]" data-id="element-155">
                <strong data-id="element-156">Flash Fiction</strong> - More than 350 words - Under
                1,000 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-157">
              <span className="text-amber-600 font-bold mt-1" data-id="element-158">•</span>
              <span data-id="element-159">
                <strong data-id="element-160">Short Story</strong> - More than 1,000 words - Under
                3,000 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-161">
              <span className="text-amber-600 font-bold mt-1" data-id="element-162">•</span>
              <span data-id="element-163">
                <strong data-id="element-164">Long Story</strong> - More than 3,000 words - Under
                10,000 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-165">
              <span className="text-amber-600 font-bold mt-1" data-id="element-166">•</span>
              <span data-id="element-167">
                <strong data-id="element-168">Really Long Story</strong> - More than 10,000 words.
              </span>
            </li>
          </ul>
        </div>

        {/* Non Fiction */}
        <div className="mb-8" data-id="element-169">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-170">
            Non Fiction
          </h3>
          <ul className="space-y-2 pl-6" data-id="element-171">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-172">
              <span className="text-amber-600 font-bold mt-1" data-id="element-173">•</span>
              <span data-id="element-174">
                <strong data-id="element-175">Short Essay</strong> - Under 500 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-176">
              <span className="text-amber-600 font-bold mt-1" data-id="element-177">•</span>
              <span data-id="element-178">
                <strong data-id="element-179">Essay</strong> - More than 500 words - Under 2,000
                words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-180">
              <span className="text-amber-600 font-bold mt-1" data-id="element-181">•</span>
              <span data-id="element-182">
                <strong data-id="element-183">Long Essay</strong> - More than 2,000 words - Under
                5,000 words.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-184">
              <span className="text-amber-600 font-bold mt-1" data-id="element-185">•</span>
              <span data-id="element-186">
                <strong data-id="element-187">Memoir</strong> - Under 3,000 words.
              </span>
            </li>
          </ul>
        </div>

        {/* Poetry */}
        <div className="mb-8" data-id="element-188">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-189">Poetry</h3>
          <ul className="space-y-2 pl-6" data-id="element-190">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-191">
              <span className="text-amber-600 font-bold mt-1" data-id="element-192">•</span>
              <span data-id="element-193">
                <strong data-id="element-194">Poetry</strong> - Unlimited, send each poem in a
                separate docx/pdf file.
              </span>
            </li>
          </ul>
        </div>

        {/* Other */}
        <div className="mb-8" data-id="element-195">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-196">Other</h3>
          <ul className="space-y-2 pl-6" data-id="element-197">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-198">
              <span className="text-amber-600 font-bold mt-1" data-id="element-199">•</span>
              <span data-id="element-200">
                <strong data-id="element-201">Other written</strong> - Plays, Screenwriting and more…
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-202">
              <span className="text-amber-600 font-bold mt-1" data-id="element-203">•</span>
              <span data-id="element-204">
                <strong data-id="element-205">Visual</strong> - Drawings, Paintings, Sketches, Videos…
              </span>
            </li>
          </ul>
        </div>
      </div>
    </>;
}
function HebrewContent() {
  return <>
      <h1 className="text-3xl md:text-5xl font-bold mb-8 text-[#2A4B7C]" data-id="element-206">
        שלח את עבודתך
      </h1>

      <p className="text-lg mb-2 font-semibold text-[#2A4B7C]" data-id="element-207">
        הנחיות - עברית
      </p>
      <p className="text-sm text-gray-500 mb-8" data-id="element-208">
        ההנחיות באנגלית נמצאות בגרסה האנגלית.
      </p>

      <div className="mb-8" data-id="element-209">
        <p className="text-gray-700 mb-2" data-id="element-210">
          על מנת לשלוח לנו את היצירה שלכם, אנא שלחו את העבודה לכתובת מייל הבאה:
        </p>
        <a href="mailto:submissions.stjournal@gmail.com" className="text-lg md:text-xl hover:text-amber-600 transition-colors font-semibold break-all text-[#374151]" data-id="element-211">
          submissions.stjournal@gmail.com
        </a>
      </div>

      <div className="mb-8" data-id="element-212">
        <p className="text-gray-700 mb-2" data-id="element-213">
          הכותרת של המייל צריכה להיות בפורמט הבא:
        </p>
        <p className="font-semibold mb-2 text-[#2A4B7C]" data-id="element-214">
          קטגוריה + שם פרטי + שם משפחה.
        </p>
        <p className="text-gray-600 italic mb-4" data-id="element-215">
          דוגמא: סיפור מהיר + איתן + לוי.
        </p>
        <p className="text-gray-700 mb-2" data-id="element-216">
          הכותרת צריכה להיות בשפת היצירה. לדוגמא, אם היצירה שלכם כתובה באנגלית,
          עליכם לוודא שכותרת ההודעה באנגלית.
        </p>
        <p className="text-gray-700" data-id="element-217">
          את היצירה יש לשלוח בקובץ <strong data-id="element-218">PDF</strong> או <strong data-id="element-219">DOCX</strong>
          .
        </p>
      </div>

      <div className="border-t border-gray-200 pt-8" data-id="element-220">
        <h2 className="text-2xl font-semibold mb-6 text-[#2A4B7C]" data-id="element-221">
          רשימת קטגוריות
        </h2>

        {/* Fiction */}
        <div className="mb-8" data-id="element-222">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-223">
            ספרות יפה
          </h3>
          <ul className="space-y-2 pr-6" data-id="element-224">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-225">
              <span className="text-amber-600 font-bold mt-1" data-id="element-226">•</span>
              <span data-id="element-227">
                <strong data-id="element-228">סיפורון (מיקרו)</strong> - מתחת ל-350 מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-229">
              <span className="text-amber-600 font-bold mt-1" data-id="element-230">•</span>
              <span data-id="element-231">
                <strong data-id="element-232">סיפור מהיר</strong> - מעל 350 מילים - מתחת ל-1,000
                מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-233">
              <span className="text-amber-600 font-bold mt-1" data-id="element-234">•</span>
              <span data-id="element-235">
                <strong data-id="element-236">סיפור קצר</strong> - מעל 1,000 מילים - מתחת ל-3,000
                מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-237">
              <span className="text-amber-600 font-bold mt-1" data-id="element-238">•</span>
              <span data-id="element-239">
                <strong data-id="element-240">סיפור ארוך</strong> - מעל 3,000 מילים - מתחת ל-10,000
                מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-241">
              <span className="text-amber-600 font-bold mt-1" data-id="element-242">•</span>
              <span data-id="element-243">
                <strong data-id="element-244">סיפור ממש ארוך</strong> - מעל 10,000 מילים.
              </span>
            </li>
          </ul>
        </div>

        {/* Non Fiction */}
        <div className="mb-8" data-id="element-245">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-246">
            ספרות עיון
          </h3>
          <ul className="space-y-2 pr-6" data-id="element-247">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-248">
              <span className="text-amber-600 font-bold mt-1" data-id="element-249">•</span>
              <span data-id="element-250">
                <strong data-id="element-251">חיבור קצר</strong> - פחות מ-500 מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-252">
              <span className="text-amber-600 font-bold mt-1" data-id="element-253">•</span>
              <span data-id="element-254">
                <strong data-id="element-255">חיבור</strong> - יותר מ-500 מילים - פחות מ-2,000 מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-256">
              <span className="text-amber-600 font-bold mt-1" data-id="element-257">•</span>
              <span data-id="element-258">
                <strong data-id="element-259">חיבור ארוך</strong> - יותר מ-2,000 מילים - פחות מ-5,000
                מילים.
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-260">
              <span className="text-amber-600 font-bold mt-1" data-id="element-261">•</span>
              <span data-id="element-262">
                <strong data-id="element-263">זיכרונות</strong> - פחות מ-3,000 מילים.
              </span>
            </li>
          </ul>
        </div>

        {/* Poetry */}
        <div className="mb-8" data-id="element-264">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-265">שירה</h3>
          <ul className="space-y-2 pr-6" data-id="element-266">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-267">
              <span className="text-amber-600 font-bold mt-1" data-id="element-268">•</span>
              <span data-id="element-269">
                <strong data-id="element-270">שירה</strong> - אין מגבלה, לשלוח כל שיר בקובץ PDF/DOCX
                נפרד.
              </span>
            </li>
          </ul>
        </div>

        {/* Other */}
        <div className="mb-8" data-id="element-271">
          <h3 className="text-xl font-semibold mb-3 text-[#2A4B7C]" data-id="element-272">אחר</h3>
          <ul className="space-y-2 pr-6" data-id="element-273">
            <li className="text-gray-700 flex items-start gap-3" data-id="element-274">
              <span className="text-amber-600 font-bold mt-1" data-id="element-275">•</span>
              <span data-id="element-276">
                <strong data-id="element-277">כתיבה אחרת</strong> - הכל כולל מחזות, תסריטים, ועוד…
              </span>
            </li>
            <li className="text-gray-700 flex items-start gap-3" data-id="element-278">
              <span className="text-amber-600 font-bold mt-1" data-id="element-279">•</span>
              <span data-id="element-280">
                <strong data-id="element-281">חזותי</strong> - ציורים, איורים, סרטונים ועוד…
              </span>
            </li>
          </ul>
        </div>
      </div>
    </>;
}
export function SubmitPage() {
  const {
    language
  } = useLanguage();
  return <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12" data-id="element-282">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-lg shadow-md p-6 md:p-12" data-id="element-283">
        {language === 'en' ? <EnglishContent data-id="element-284" /> : <HebrewContent data-id="element-285" />}
      </motion.div>
    </div>;
}